import {
  DemoBatchTicket,
  DemoChecklist,
  DemoCylinderBreak,
  DemoCylinderSample,
  DemoDamageIncident,
  DemoEngineeringRepair,
  DemoInspectionEvent,
  DemoPanelRejection,
  DemoScheduledInspection,
  DemoYardPiece,
} from './qcDemoTypes';

const today = new Date();
const isoToday = (hours: number, minutes = 0): string => {
  const d = new Date(today);
  d.setHours(hours, minutes, 0, 0);
  return d.toISOString();
};
const isoOffset = (daysAgo: number, hours = 9): string => {
  const d = new Date(today);
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hours, 0, 0, 0);
  return d.toISOString();
};
const dateOnly = (daysFromToday: number): string => {
  const d = new Date(today);
  d.setDate(d.getDate() + daysFromToday);
  return d.toISOString().slice(0, 10);
};

export const seedInspectionEvents: DemoInspectionEvent[] = [
  {
    inspectionId: 1001,
    panelId: 8101,
    panelMark: 'RC-A1.1',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    inspectionDatetime: isoOffset(1, 8),
    inspectionStage: 'pre_casting',
    inspectionType: 'mold',
    passFail: 'pass',
    durationMinutes: 25,
    inspectorId: 12,
    inspectorName: 'James Carter',
    notes: 'Mold prepped clean, embeds verified per drawing.',
    approvedForNextStage: true,
    createdAt: isoOffset(1, 8),
    updatedAt: isoOffset(1, 8),
  },
  {
    inspectionId: 1002,
    panelId: 8102,
    panelMark: 'RC-A1.2',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    inspectionDatetime: isoOffset(1, 10),
    inspectionStage: 'pre_casting',
    inspectionType: 'mold',
    passFail: 'pass',
    durationMinutes: 18,
    inspectorId: 12,
    inspectorName: 'James Carter',
    approvedForNextStage: true,
    createdAt: isoOffset(1, 10),
    updatedAt: isoOffset(1, 10),
  },
  {
    inspectionId: 1003,
    panelId: 8103,
    panelMark: 'HIL-B2.1',
    projectId: 502,
    projectName: 'Hilton Brickell 23-104',
    inspectionDatetime: isoOffset(2, 14),
    inspectionStage: 'post_stripping',
    inspectionType: 'dimensional',
    passFail: 'fail',
    durationMinutes: 45,
    failureCategory: 'Dimensional',
    defectsNoted: 'Length 1/4" over tolerance on north edge.',
    correctiveActionRequired: true,
    correctiveAction: 'Grind to tolerance, re-inspect.',
    reInspectionRequired: true,
    inspectorId: 14,
    inspectorName: 'Maria Lopez',
    notes: 'Photo on file. Notified production lead.',
    createdAt: isoOffset(2, 14),
    updatedAt: isoOffset(2, 14),
  },
  {
    inspectionId: 1004,
    panelId: 8104,
    panelMark: 'HIL-B2.2',
    projectId: 502,
    projectName: 'Hilton Brickell 23-104',
    inspectionDatetime: isoOffset(3, 11),
    inspectionStage: 'post_finishing',
    inspectionType: 'product',
    passFail: 'pass',
    durationMinutes: 32,
    inspectorId: 14,
    inspectorName: 'Maria Lopez',
    approvedForNextStage: true,
    createdAt: isoOffset(3, 11),
    updatedAt: isoOffset(3, 11),
  },
  {
    inspectionId: 1005,
    panelId: 8105,
    panelMark: 'RC-C3.1',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    inspectionDatetime: isoOffset(0, 9),
    inspectionStage: 'pre_casting',
    inspectionType: 'mold',
    passFail: 'pass',
    durationMinutes: 22,
    inspectorId: 12,
    inspectorName: 'James Carter',
    approvedForNextStage: true,
    createdAt: isoOffset(0, 9),
    updatedAt: isoOffset(0, 9),
  },
];

export const seedRejections: DemoPanelRejection[] = [
  {
    rejectionId: 5001,
    panelId: 8103,
    panelMark: 'HIL-B2.1',
    projectId: 502,
    projectName: 'Hilton Brickell 23-104',
    qcInspectionId: 1003,
    reportedAt: isoOffset(2, 14),
    reportedBy: 14,
    reportedByName: 'Maria Lopez',
    reason: 'Length out of tolerance on north edge (+1/4"). Grind-to-fit possible but requires re-cure cycle.',
    failureCategory: 'Dimensional',
    status: 'open',
    notes: 'Awaiting plant manager review for reschedule.',
    createdAt: isoOffset(2, 14),
    updatedAt: isoOffset(2, 14),
  },
  {
    rejectionId: 5002,
    panelId: 8106,
    panelMark: 'OCM-D1.3',
    projectId: 503,
    projectName: 'Ocean Manor 22-301',
    reportedAt: isoOffset(5, 10),
    reportedBy: 14,
    reportedByName: 'Maria Lopez',
    reason: 'Honeycomb on architectural face, finish team unable to repair without compromising rib geometry.',
    failureCategory: 'Surface',
    status: 'rescheduled',
    rescheduledNewDate: dateOnly(3),
    rescheduledApprovedBy: 21,
    rescheduledApprovedByName: 'Daniel Reyes',
    rescheduledApprovedAt: isoOffset(4, 16),
    notes: 'Mold cleaned and retreated; new pour scheduled.',
    createdAt: isoOffset(5, 10),
    updatedAt: isoOffset(4, 16),
  },
  {
    rejectionId: 5003,
    panelId: 8107,
    panelMark: 'HIL-A4.2',
    projectId: 502,
    projectName: 'Hilton Brickell 23-104',
    reportedAt: isoOffset(8, 9),
    reportedBy: 12,
    reportedByName: 'James Carter',
    reason: 'Embed misalignment caught at pre-pour; cage already tied around it.',
    failureCategory: 'Embed',
    status: 'dismissed',
    dismissedAt: isoOffset(7, 11),
    dismissedBy: 21,
    dismissedByName: 'Daniel Reyes',
    notes: 'Re-confirmed embed position is within engineering tolerance after design review.',
    createdAt: isoOffset(8, 9),
    updatedAt: isoOffset(7, 11),
  },
];

export const seedScheduledInspections: DemoScheduledInspection[] = [
  {
    scheduledId: 9001,
    panelId: 8108,
    panelMark: 'RC-C3.2',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    moldId: 'MA1.2',
    scheduledStage: 'pre_casting',
    scheduledDate: dateOnly(0),
    qcStatus: 'Pending',
  },
  {
    scheduledId: 9002,
    panelId: 8109,
    panelMark: 'RC-C3.3',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    moldId: 'MA1.3',
    scheduledStage: 'pre_casting',
    scheduledDate: dateOnly(0),
    qcStatus: 'Pending',
  },
  {
    scheduledId: 9003,
    panelId: 8110,
    panelMark: 'HIL-B3.1',
    projectId: 502,
    projectName: 'Hilton Brickell 23-104',
    moldId: 'MB3.1',
    scheduledStage: 'during_casting',
    scheduledDate: dateOnly(0),
    qcStatus: 'InProgress',
  },
  {
    scheduledId: 9004,
    panelId: 8111,
    panelMark: 'HIL-B3.2',
    projectId: 502,
    projectName: 'Hilton Brickell 23-104',
    moldId: 'MB3.2',
    scheduledStage: 'during_casting',
    scheduledDate: dateOnly(0),
    qcStatus: 'Pending',
  },
  {
    scheduledId: 9005,
    panelId: 8112,
    panelMark: 'OCM-D2.1',
    projectId: 503,
    projectName: 'Ocean Manor 22-301',
    moldId: 'MD2.1',
    scheduledStage: 'post_stripping',
    scheduledDate: dateOnly(0),
    qcStatus: 'Pending',
  },
  {
    scheduledId: 9006,
    panelId: 8113,
    panelMark: 'OCM-D2.2',
    projectId: 503,
    projectName: 'Ocean Manor 22-301',
    moldId: 'MD2.2',
    scheduledStage: 'post_stripping',
    scheduledDate: dateOnly(0),
    qcStatus: 'PrePourCompleted',
  },
  {
    scheduledId: 9007,
    panelId: 8114,
    panelMark: 'RC-A2.1',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    moldId: 'MA2.1',
    scheduledStage: 'post_finishing',
    scheduledDate: dateOnly(0),
    qcStatus: 'Yellow',
  },
  {
    scheduledId: 9008,
    panelId: 8115,
    panelMark: 'RC-A2.2',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    moldId: 'MA2.2',
    scheduledStage: 'pre_shipping',
    scheduledDate: dateOnly(0),
    qcStatus: 'Green',
  },
  {
    scheduledId: 9009,
    panelId: 8116,
    panelMark: 'HIL-A3.1',
    projectId: 502,
    projectName: 'Hilton Brickell 23-104',
    moldId: 'MB1.1',
    scheduledStage: 'pre_shipping',
    scheduledDate: dateOnly(0),
    qcStatus: 'Green',
  },
];

// ============================================================================
// Checklists
// ============================================================================

export const seedChecklists: DemoChecklist[] = [
  {
    checklistId: 1,
    name: 'Pre-Cast Mold Check',
    stage: 'pre_casting',
    description: 'Verify mold prep before any concrete arrives.',
    active: true,
    updatedAt: isoOffset(15, 9),
    items: [
      { itemId: 11, text: 'Mold surface clean and free of debris', required: true, allowsNA: false },
      { itemId: 12, text: 'Release agent applied evenly', required: true, allowsNA: false },
      { itemId: 13, text: 'Embeds match drawing and position', required: true, allowsNA: false },
      { itemId: 14, text: 'Rebar cage in place and tied off', required: true, allowsNA: false },
      { itemId: 15, text: 'Cover blocks installed at required spacing', required: true, allowsNA: false },
      { itemId: 16, text: 'Architectural features verified per drawing', required: false, allowsNA: true },
      { itemId: 17, text: 'Cast date stamped on mold', required: false, allowsNA: true },
      { itemId: 18, text: 'Mold ID tag visible', required: true, allowsNA: false },
    ],
  },
  {
    checklistId: 2,
    name: 'Pre-Pour Embed Verify',
    stage: 'during_casting',
    description: 'Final embed and reinforcement check before pour starts.',
    active: true,
    updatedAt: isoOffset(20, 9),
    items: [
      { itemId: 21, text: 'Anchor bolts torqued to spec', required: true, allowsNA: false },
      { itemId: 22, text: 'Lifting inserts at correct height', required: true, allowsNA: false },
      { itemId: 23, text: 'PT ducts clear and aligned', required: false, allowsNA: true },
      { itemId: 24, text: 'Sleeve openings present and dimensioned', required: true, allowsNA: false },
      { itemId: 25, text: 'Concrete cylinder forms prepared', required: true, allowsNA: false },
    ],
  },
  {
    checklistId: 3,
    name: 'Post-Strip Dimensional',
    stage: 'post_stripping',
    description: 'Full dimensional inspection after stripping.',
    active: true,
    updatedAt: isoOffset(8, 11),
    items: [
      { itemId: 31, text: 'Length within tolerance (+/- 1/8")', required: true, allowsNA: false },
      { itemId: 32, text: 'Width within tolerance', required: true, allowsNA: false },
      { itemId: 33, text: 'Thickness within tolerance', required: true, allowsNA: false },
      { itemId: 34, text: 'Squareness check passes', required: true, allowsNA: false },
      { itemId: 35, text: 'Embed locations within tolerance', required: true, allowsNA: false },
      { itemId: 36, text: 'No spalls > 1/2"', required: true, allowsNA: false },
      { itemId: 37, text: 'No honeycomb on architectural face', required: true, allowsNA: false },
      { itemId: 38, text: 'Bug holes within acceptable density', required: true, allowsNA: false },
      { itemId: 39, text: 'Corner chips < 1/4"', required: false, allowsNA: true },
      { itemId: 310, text: 'Surface uniformity acceptable', required: true, allowsNA: false },
      { itemId: 311, text: 'Reinforcement not exposed', required: true, allowsNA: false },
      { itemId: 312, text: 'Cast date stamp visible', required: false, allowsNA: true },
    ],
  },
  {
    checklistId: 4,
    name: 'Finish Quality Audit',
    stage: 'post_finishing',
    description: 'Final finish QC before piece moves to yard.',
    active: true,
    updatedAt: isoOffset(12, 11),
    items: [
      { itemId: 41, text: 'Finish matches approved sample', required: true, allowsNA: false },
      { itemId: 42, text: 'Color uniformity acceptable', required: true, allowsNA: false },
      { itemId: 43, text: 'Edges chamfered per drawing', required: true, allowsNA: false },
      { itemId: 44, text: 'Sealer applied (if specified)', required: false, allowsNA: true },
      { itemId: 45, text: 'No exposed reinforcement', required: true, allowsNA: false },
      { itemId: 46, text: 'Patch work blended', required: false, allowsNA: true },
      { itemId: 47, text: 'No staining or efflorescence', required: true, allowsNA: false },
      { itemId: 48, text: 'Piece ID stamp visible', required: true, allowsNA: false },
      { itemId: 49, text: 'Lifting inserts capped', required: true, allowsNA: false },
    ],
  },
  {
    checklistId: 5,
    name: 'Pre-Ship Final Check',
    stage: 'pre_shipping',
    description: 'Final inspection prior to loading on truck.',
    active: true,
    updatedAt: isoOffset(5, 14),
    items: [
      { itemId: 51, text: 'No transit-prep damage', required: true, allowsNA: false },
      { itemId: 52, text: 'Piece ID matches paperwork', required: true, allowsNA: false },
      { itemId: 53, text: 'Lifting inserts undamaged', required: true, allowsNA: false },
      { itemId: 54, text: 'Customer-required photos taken', required: false, allowsNA: true },
      { itemId: 55, text: 'Shipping tag affixed', required: true, allowsNA: false },
      { itemId: 56, text: 'Packing/dunnage prepared', required: false, allowsNA: true },
      { itemId: 57, text: 'Final QC sign-off', required: true, allowsNA: false },
    ],
  },
];

// ============================================================================
// Concrete testing — batch tickets, samples, breaks
// ============================================================================

export const seedBatchTickets: DemoBatchTicket[] = [
  {
    batchTicketId: 7001,
    ticketNumber: 'BT-2401',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    mixCode: '8500-A',
    targetStrengthPsi: 8500,
    pouredAt: isoOffset(0, 9),
    yards: 8.5,
    slumpInches: 6.5,
    airPercent: 5.2,
    concreteTempF: 72,
    ambientTempF: 68,
    passFail: 'pass',
    withinSpec: true,
    supplier: 'Atlantic Ready-Mix',
    truckNumber: 'AR-118',
    driver: 'Ricky Donnelly',
    pouredByName: 'James Carter',
    linkedPanelIds: [8108, 8109, 8101],
    notes: 'Pour temp warm; chilled mix water used.',
  },
  {
    batchTicketId: 7002,
    ticketNumber: 'BT-2402',
    projectId: 502,
    projectName: 'Hilton Brickell 23-104',
    mixCode: '7000-S',
    targetStrengthPsi: 7000,
    pouredAt: isoOffset(0, 11, 30),
    yards: 6.0,
    slumpInches: 5.5,
    airPercent: 4.8,
    concreteTempF: 70,
    ambientTempF: 69,
    passFail: 'pass',
    withinSpec: true,
    supplier: 'Atlantic Ready-Mix',
    truckNumber: 'AR-122',
    driver: 'Mark Bell',
    pouredByName: 'Maria Lopez',
    linkedPanelIds: [8110, 8111],
  },
  {
    batchTicketId: 7003,
    ticketNumber: 'BT-2387',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    mixCode: '8500-A',
    targetStrengthPsi: 8500,
    pouredAt: isoOffset(2, 8, 45),
    yards: 9.0,
    slumpInches: 8.5,
    airPercent: 6.4,
    concreteTempF: 78,
    ambientTempF: 84,
    passFail: 'fail',
    withinSpec: false,
    supplier: 'Atlantic Ready-Mix',
    truckNumber: 'AR-114',
    driver: 'Ricky Donnelly',
    pouredByName: 'James Carter',
    linkedPanelIds: [8103],
    notes: 'Slump high; load rejected — see follow-up batch BT-2388.',
  },
  {
    batchTicketId: 7004,
    ticketNumber: 'BT-2388',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    mixCode: '8500-A',
    targetStrengthPsi: 8500,
    pouredAt: isoOffset(2, 10, 5),
    yards: 9.0,
    slumpInches: 6.75,
    airPercent: 5.0,
    concreteTempF: 73,
    ambientTempF: 84,
    passFail: 'pass',
    withinSpec: true,
    supplier: 'Atlantic Ready-Mix',
    truckNumber: 'AR-117',
    driver: 'Tony Russo',
    pouredByName: 'James Carter',
    linkedPanelIds: [8103],
  },
  {
    batchTicketId: 7005,
    ticketNumber: 'BT-UHPC-0042',
    projectId: 503,
    projectName: 'Ocean Manor 22-301',
    mixCode: '10000-UHPC',
    targetStrengthPsi: 10000,
    pouredAt: isoOffset(7, 9),
    yards: 3.5,
    slumpInches: 7.0,
    airPercent: 2.8,
    concreteTempF: 71,
    passFail: 'pass',
    withinSpec: true,
    supplier: 'Premier In-House',
    pouredByName: 'Maria Lopez',
    linkedPanelIds: [8112, 8113],
    notes: 'UHPC mix — extended cure schedule.',
  },
];

export const seedCylinderSamples: DemoCylinderSample[] = [
  {
    sampleId: 8001,
    sampleNumber: 'CYL-2401-A',
    batchTicketId: 7001,
    takenAt: isoOffset(0, 9, 15),
    cylinderCount: 6,
    storageLocation: 'Curing room A',
    takenByName: 'James Carter',
  },
  {
    sampleId: 8002,
    sampleNumber: 'CYL-2402-A',
    batchTicketId: 7002,
    takenAt: isoOffset(0, 11, 45),
    cylinderCount: 6,
    storageLocation: 'Curing room A',
    takenByName: 'Maria Lopez',
  },
  {
    sampleId: 8003,
    sampleNumber: 'CYL-2388-A',
    batchTicketId: 7004,
    takenAt: isoOffset(2, 10, 20),
    cylinderCount: 6,
    storageLocation: 'Curing room B',
    takenByName: 'James Carter',
  },
  {
    sampleId: 8004,
    sampleNumber: 'CYL-UHPC-0042',
    batchTicketId: 7005,
    takenAt: isoOffset(7, 9, 30),
    cylinderCount: 9,
    storageLocation: 'UHPC cure cabinet',
    takenByName: 'Maria Lopez',
  },
];

export const seedCylinderBreaks: DemoCylinderBreak[] = [
  // BT-2388 sample - 7-day break done, 28-day pending
  {
    breakId: 9001,
    sampleId: 8003,
    breakAge: 7,
    scheduledDate: dateOnly(-2 + 7),
    brokenAt: isoOffset(-5, 14),
    strengthPsi: 5840,
    percentOfTarget: 68.7,
    passFail: 'pass',
    brokenByName: 'Maria Lopez',
  },
  {
    breakId: 9002,
    sampleId: 8003,
    breakAge: 28,
    scheduledDate: dateOnly(-2 + 28),
  },
  // BT-2401 sample - both pending
  {
    breakId: 9003,
    sampleId: 8001,
    breakAge: 7,
    scheduledDate: dateOnly(7),
  },
  {
    breakId: 9004,
    sampleId: 8001,
    breakAge: 28,
    scheduledDate: dateOnly(28),
  },
  // BT-2402 sample - both pending
  {
    breakId: 9005,
    sampleId: 8002,
    breakAge: 7,
    scheduledDate: dateOnly(7),
  },
  {
    breakId: 9006,
    sampleId: 8002,
    breakAge: 28,
    scheduledDate: dateOnly(28),
  },
  // UHPC sample - 7-day done, 28-day done, 56-day pending
  {
    breakId: 9007,
    sampleId: 8004,
    breakAge: 7,
    scheduledDate: dateOnly(-7 + 7),
    brokenAt: isoOffset(0, 11),
    strengthPsi: 7100,
    percentOfTarget: 71.0,
    passFail: 'pass',
    brokenByName: 'Maria Lopez',
    notes: 'Strong 7-day for UHPC; on track.',
  },
  {
    breakId: 9008,
    sampleId: 8004,
    breakAge: 28,
    scheduledDate: dateOnly(-7 + 28),
  },
  {
    breakId: 9009,
    sampleId: 8004,
    breakAge: 56,
    scheduledDate: dateOnly(-7 + 56),
  },
];

// ============================================================================
// Engineering Repair (NCR)
// ============================================================================

export const seedEngineeringRepairs: DemoEngineeringRepair[] = [
  {
    repairId: 4001,
    rejectionId: 5001,
    panelId: 8103,
    panelMark: 'HIL-B2.1',
    projectId: 502,
    projectName: 'Hilton Brickell 23-104',
    status: 'awaiting_design',
    sentToEngineeringAt: isoOffset(1, 9),
    sentByName: 'Daniel Reyes',
    notes: 'Dim overage on north edge — confirm if grind/patch is sufficient or new pour required.',
  },
  {
    repairId: 4002,
    rejectionId: 5099,
    panelId: 8120,
    panelMark: 'OCM-D1.5',
    projectId: 503,
    projectName: 'Ocean Manor 22-301',
    status: 'repair_in_progress',
    sentToEngineeringAt: isoOffset(4, 9),
    sentByName: 'Daniel Reyes',
    repairPlan: 'Grind 1/4" on north 4" of edge, patch with high-strength epoxy mortar. Re-inspect post-cure (48hr).',
    repairPlanByName: 'Sarah Engineering',
    repairPlanAt: isoOffset(3, 14),
    repairStartedAt: isoOffset(2, 8),
  },
  {
    repairId: 4003,
    rejectionId: 5098,
    panelId: 8121,
    panelMark: 'HIL-B5.3',
    projectId: 502,
    projectName: 'Hilton Brickell 23-104',
    status: 'awaiting_reinspection',
    sentToEngineeringAt: isoOffset(8, 9),
    sentByName: 'Daniel Reyes',
    repairPlan: 'Surface honeycomb patched with matching mortar; texture reproduced with stamp.',
    repairPlanByName: 'Sarah Engineering',
    repairPlanAt: isoOffset(7, 11),
    repairStartedAt: isoOffset(6, 8),
    repairCompletedAt: isoOffset(2, 16),
    repairCompletedByName: 'Hector Velasquez',
  },
  {
    repairId: 4004,
    rejectionId: 5097,
    panelId: 8122,
    panelMark: 'RC-D2.1',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    status: 'closed_repaired',
    sentToEngineeringAt: isoOffset(20, 9),
    sentByName: 'Daniel Reyes',
    repairPlan: 'Re-tie cage offset; pour acceptable per spec.',
    repairPlanByName: 'Sarah Engineering',
    repairPlanAt: isoOffset(19, 14),
    repairStartedAt: isoOffset(18, 8),
    repairCompletedAt: isoOffset(14, 16),
    repairCompletedByName: 'Hector Velasquez',
    reInspectionId: 1099,
    closedAt: isoOffset(13, 10),
    closedByName: 'Maria Lopez',
    notes: 'Closed after passing re-inspection on 2026-04-30.',
  },
];

// ============================================================================
// Yard QC
// ============================================================================

export const seedYardPieces: DemoYardPiece[] = [
  {
    yardId: 6001,
    panelId: 8115,
    panelMark: 'RC-A2.2',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    bay: 'Bay 4',
    arrivedAt: isoOffset(3, 14),
    scheduledShipDate: dateOnly(2),
    status: 'ready_to_ship',
  },
  {
    yardId: 6002,
    panelId: 8116,
    panelMark: 'HIL-A3.1',
    projectId: 502,
    projectName: 'Hilton Brickell 23-104',
    bay: 'Bay 7',
    arrivedAt: isoOffset(2, 14),
    scheduledShipDate: dateOnly(2),
    status: 'ready_to_ship',
  },
  {
    yardId: 6003,
    panelId: 8130,
    panelMark: 'RC-B1.1',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    bay: 'Bay 1',
    arrivedAt: isoOffset(5, 14),
    scheduledShipDate: dateOnly(3),
    status: 'pending_final_check',
  },
  {
    yardId: 6004,
    panelId: 8131,
    panelMark: 'RC-B1.2',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    bay: 'Bay 1',
    arrivedAt: isoOffset(5, 14),
    scheduledShipDate: dateOnly(3),
    status: 'pending_final_check',
  },
  {
    yardId: 6005,
    panelId: 8132,
    panelMark: 'HIL-B4.2',
    projectId: 502,
    projectName: 'Hilton Brickell 23-104',
    bay: 'Bay 2',
    arrivedAt: isoOffset(7, 14),
    scheduledShipDate: dateOnly(4),
    status: 'pending_final_check',
  },
  {
    yardId: 6006,
    panelId: 8133,
    panelMark: 'RC-B4.1',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    bay: 'Bay 9',
    arrivedAt: isoOffset(10, 14),
    status: 'damaged',
    notes: 'Forklift contact — see damage incident.',
  },
  {
    yardId: 6007,
    panelId: 8134,
    panelMark: 'OCM-D2.2',
    projectId: 503,
    projectName: 'Ocean Manor 22-301',
    bay: 'Bay 2',
    arrivedAt: isoOffset(12, 14),
    scheduledShipDate: dateOnly(6),
    status: 'ready_to_ship',
  },
  {
    yardId: 6008,
    panelId: 8135,
    panelMark: 'OCM-D2.1',
    projectId: 503,
    projectName: 'Ocean Manor 22-301',
    bay: 'Bay 2',
    arrivedAt: isoOffset(12, 14),
    scheduledShipDate: dateOnly(6),
    status: 'ready_to_ship',
  },
];

export const seedDamageIncidents: DemoDamageIncident[] = [
  {
    incidentId: 7501,
    panelId: 8133,
    panelMark: 'RC-B4.1',
    projectId: 501,
    projectName: 'Royal Caribbean 22-217',
    reportedAt: isoOffset(1, 14),
    reportedByName: 'Hector Velasquez',
    severity: 'moderate',
    cause: 'Forklift contact',
    description: 'Forklift tine struck north corner during repositioning. Chip approx 2"x3", depth 1/2".',
    outcome: 'send_to_engineering',
    photoCount: 4,
    signedOffByName: 'Daniel Reyes',
    notes: 'Awaiting engineering disposition.',
  },
  {
    incidentId: 7502,
    panelId: 8140,
    panelMark: 'HIL-C1.1',
    projectId: 502,
    projectName: 'Hilton Brickell 23-104',
    reportedAt: isoOffset(15, 9),
    reportedByName: 'Hector Velasquez',
    severity: 'minor',
    cause: 'Weather (hail)',
    description: 'Surface pitting on architectural face from spring hailstorm. Pitting < 1/16".',
    outcome: 'repair_in_house',
    photoCount: 2,
    signedOffByName: 'Daniel Reyes',
    notes: 'Buff and reseal in-house.',
  },
  {
    incidentId: 7503,
    panelId: 8141,
    panelMark: 'OCM-A5.4',
    projectId: 503,
    projectName: 'Ocean Manor 22-301',
    reportedAt: isoOffset(30, 11),
    reportedByName: 'Hector Velasquez',
    severity: 'severe',
    cause: 'Transit prep / rigging',
    description: 'Lifting insert pulled out during pre-load test lift. Insert and surrounding concrete spalled.',
    outcome: 'rejected_replace',
    photoCount: 6,
    signedOffByName: 'Daniel Reyes',
    notes: 'Piece scrapped; replacement scheduled.',
  },
];
